/**
 * Mobile-specific HUD adaptations.
 * Detects mobile devices and injects responsive CSS overrides for the game HUD.
 * Includes a control mode selector UI for choosing between Simple, Advanced, and Custom controls.
 */

import { settingsStore, type ControlMode } from '../core/settings';
import { isMobile } from '../core/device';

/**
 * Apply mobile-specific HUD responsive CSS overrides.
 * Only injects styles on mobile/touch devices.
 *
 * BUG-MT-12 / BUG-MT-31: Use the shared `isMobile()` from `../core/device`
 * instead of a local `'ontouchstart' in window` heuristic. The shared
 * detector excludes hover/fine-pointer touch laptops, preventing a
 * half-state UI where mobile HUD CSS was applied but no touch input
 * controls were available.
 *
 * @returns A cleanup function that removes the injected style tag,
 *          or a no-op function on desktop.
 */
export function applyMobileHUD(): () => void {
  if (!isMobile()) {
    return () => {};
  }

  const styleEl = document.createElement('style');
  styleEl.id = 'mobile-hud-styles';
  styleEl.textContent = `
    /* ── Mobile HUD Responsive Overrides ── */

    /* Prevent touch scrolling/zooming on the game canvas */
    #game-canvas {
      touch-action: none;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }

    /* Safe area padding for the HUD container */
    .hud-container,
    #hud {
      padding-top: env(safe-area-inset-top, 0px);
      padding-bottom: env(safe-area-inset-bottom, 0px);
      padding-left: env(safe-area-inset-left, 0px);
      padding-right: env(safe-area-inset-right, 0px);
    }

    /* Responsive font sizes using clamp() */
    [data-hud-element="health"] #health-value,
    [data-hud-element="health"] #armor-value {
      font-size: clamp(14px, 4vw, 24px);
    }

    [data-hud-element="ammo"] {
      font-size: clamp(14px, 4vw, 24px);
    }

    [data-hud-element="ammo"] #ammo-current {
      font-size: clamp(14px, 4vw, 24px);
    }

    [data-hud-element="ammo"] #ammo-reserve {
      font-size: clamp(12px, 3vw, 20px);
    }

    [data-hud-element="timer"] {
      font-size: clamp(14px, 3.5vw, 22px);
    }

    [data-hud-element="killfeed"] {
      font-size: clamp(10px, 2.5vw, 13px);
    }

    /* Responsive minimap sizing — shrink both width and height on mobile so
       the BUY button (left:3% top:12%) no longer overlaps. */
    [data-hud-element="minimap"],
    #minimap-canvas,
    #minimap {
      width: clamp(80px, 14vw, 140px) !important;
      height: clamp(80px, 14vw, 140px) !important;
      top: calc(12px + env(safe-area-inset-top, 0px)) !important;
      left: calc(12px + env(safe-area-inset-left, 0px)) !important;
    }

    /* Health/ammo bars — responsive positioning with safe areas */
    #health-bar {
      bottom: calc(14px + env(safe-area-inset-bottom, 0px));
      left: calc(14px + env(safe-area-inset-left, 0px));
      padding: clamp(4px, 1vw, 6px) clamp(8px, 2vw, 14px);
    }

    #ammo-display {
      bottom: calc(14px + env(safe-area-inset-bottom, 0px));
      right: calc(14px + env(safe-area-inset-right, 0px));
      padding: clamp(4px, 1vw, 6px) clamp(8px, 2vw, 14px);
    }

    #weapon-name {
      bottom: calc(48px + env(safe-area-inset-bottom, 0px));
      right: calc(16px + env(safe-area-inset-right, 0px));
      font-size: clamp(10px, 2vw, 13px);
    }

    /* Timer / round score — use dynamic viewport units where possible */
    #timer {
      top: calc(12px + env(safe-area-inset-top, 0px));
    }

    #round-score {
      top: calc(42px + env(safe-area-inset-top, 0px));
    }

    #minimap-canvas,
    #minimap {
      top: calc(12px + env(safe-area-inset-top, 0px));
      left: calc(12px + env(safe-area-inset-left, 0px));
    }

    #kill-feed {
      top: calc(12px + env(safe-area-inset-top, 0px));
      right: calc(12px + env(safe-area-inset-right, 0px));
    }

    #money-display {
      right: calc(20px + env(safe-area-inset-right, 0px));
      font-size: clamp(14px, 3vw, 20px);
    }

    #debug-info {
      font-size: clamp(8px, 1.8vw, 11px);
    }

    /* Touch button base class */
    .touch-button {
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.12);
      border: 2px solid rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.7);
      font-family: 'Courier New', monospace;
      font-size: 12px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 1px;
      touch-action: none;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
      pointer-events: auto;
      z-index: 20;
      transition: background 0.1s, border-color 0.1s;
    }

    .touch-button:active {
      background: rgba(255, 255, 255, 0.25);
      border-color: rgba(255, 255, 255, 0.5);
    }

    /* Prevent body scrolling on mobile */
    body {
      overscroll-behavior: none;
      -webkit-overflow-scrolling: touch;
    }

    /* Use dvh/dvw for fullscreen elements when available */
    @supports (height: 100dvh) {
      #main-menu,
      #death-screen-overlay,
      #rotate-overlay {
        height: 100dvh;
        width: 100dvw;
      }
    }

    /* F-MOB-V1: narrow viewports (<360px) stack scope/reload/ads vertically with
       a wider right offset so they don't overlap each other. The base inline
       styles (right:3% scope / right:8% reload+ads) still drive widths. We use
       !important here because each button has its right value baked into an
       inline style at construction time. */
    @media (max-width: 359.98px) {
      #btn-scope {
        right: calc(3% + env(safe-area-inset-right, 0px)) !important;
        bottom: calc(70% + env(safe-area-inset-bottom, 0px)) !important;
      }
      #btn-reload {
        right: calc(3% + env(safe-area-inset-right, 0px)) !important;
        bottom: calc(56% + env(safe-area-inset-bottom, 0px)) !important;
      }
      #btn-ads {
        right: calc(3% + env(safe-area-inset-right, 0px)) !important;
        bottom: calc(42% + env(safe-area-inset-bottom, 0px)) !important;
      }
      #btn-fire {
        right: calc(3% + env(safe-area-inset-right, 0px)) !important;
      }
    }

    /* Scoreboard responsiveness */
    #scoreboard {
      min-width: auto;
      width: clamp(300px, 85vw, 520px);
      padding: clamp(10px, 2vw, 18px) clamp(12px, 3vw, 24px);
    }

    #scoreboard th,
    #scoreboard td {
      font-size: clamp(10px, 2vw, 13px);
      padding: clamp(3px, 0.8vw, 5px) clamp(4px, 1vw, 8px);
    }

    /* Main menu responsiveness */
    #main-menu h1 {
      font-size: clamp(20px, 6vw, 44px);
      letter-spacing: clamp(2px, 1vw, 8px);
    }

    #main-menu .subtitle {
      font-size: clamp(10px, 2vw, 14px);
      letter-spacing: clamp(2px, 1vw, 6px);
    }

    #main-menu button {
      padding: clamp(10px, 2vw, 14px) clamp(30px, 6vw, 55px);
      font-size: clamp(12px, 2.5vw, 16px);
    }

    /* .play-button is a div/anchor, not a <button> — separate selector
       so it actually receives responsive sizing on small screens. */
    .play-button {
      font-size: clamp(14px, 2.8vw, 18px);
      padding: clamp(10px, 2.5vw, 16px) clamp(24px, 5vw, 48px);
    }

    /* Skin selector responsive grid */
    #skin-selector {
      flex-direction: column;
      align-items: center;
      gap: 10px;
      max-width: 90vw;
    }

    /* Mobile baseline (touch device but not yet at the 480px breakpoint):
       3-up grid with an 8px gap so cards aren't squished against each other
       and have enough surrounding space to tap cleanly. */
    #skin-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      max-height: 200px;
    }

    #skin-preview-canvas {
      width: clamp(120px, 25vw, 200px);
      height: clamp(120px, 25vw, 200px);
    }

    /* Narrow-screen menu chrome — collapse the sidebar, hide status, shrink
       chrome paddings so the title and play button get the full width. The
       block also overrides the mobile baseline above for very-narrow phones,
       so it must come AFTER the baseline rules to win specificity ties. */
    @media (max-width: 480px) {
      #main-menu .menu-sidebar { width: 50px; }
      #main-menu .menu-content { padding: 12px; }
      #main-menu .menu-status { display: none; }
      .menu-controls { font-size: clamp(9px, 2.2vw, 11px); }
      /* Very-narrow phones (≤480px): drop to 2-up cards so each thumbnail
         is wide enough to be a comfortable thumb target, and enlarge the
         canvas to a tappable minimum height (≥96px clears the 44px guideline
         with margin). */
      #skin-grid { grid-template-columns: repeat(2, 1fr); }
      .ss-card canvas { height: clamp(96px, 24vw, 120px); }
    }
  `;
  document.head.appendChild(styleEl);

  // ── Orientation reflow ──
  // Listen for landscape <-> portrait transitions and toggle a body class
  // so responsive CSS / module-level layout code can react. Without this
  // continuous listener the HUD would only reflect the orientation at the
  // moment applyMobileHUD() ran.
  const mql =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(orientation: landscape)')
      : null;

  const applyOrientation = (matches: boolean) => {
    document.body.classList.toggle('hud-landscape', matches);
    document.body.classList.toggle('hud-portrait', !matches);
  };

  let orientationListener: ((e: MediaQueryListEvent) => void) | null = null;
  if (mql) {
    applyOrientation(!!mql.matches);
    orientationListener = (e: MediaQueryListEvent) => applyOrientation(e.matches);
    mql.addEventListener('change', orientationListener);
  }

  let cleaned = false;
  return () => {
    if (cleaned) return; // Idempotent — safe to call multiple times
    cleaned = true;
    styleEl.remove();
    if (mql && orientationListener) {
      mql.removeEventListener('change', orientationListener);
      orientationListener = null;
    }
    document.body.classList.remove('hud-landscape', 'hud-portrait');
  };
}

/**
 * Request fullscreen and lock orientation to landscape.
 * Handles iOS and Android differences gracefully.
 */
export async function requestFullscreenLandscape(): Promise<void> {
  const docEl = document.documentElement;

  // Request fullscreen
  if (docEl.requestFullscreen) {
    await docEl.requestFullscreen();
  } else if ((docEl as any).webkitRequestFullscreen) {
    await (docEl as any).webkitRequestFullscreen();
  }

  // Try to lock orientation to landscape (not supported on iOS)
  // screen.orientation.lock() exists in the Web API but is not in all TS DOM lib typings
  try {
    const orientation = screen.orientation as any;
    if (orientation && typeof orientation.lock === 'function') {
      await orientation.lock('landscape');
    }
  } catch {
    // Orientation lock not supported (e.g., iOS Safari) — silently ignore
  }
}

// ── Control Mode Selector ──

/** Metadata for each control mode. */
export interface ControlModeInfo {
  mode: ControlMode;
  label: string;
  fire: string;
  ads: string;
  target: string;
}

export const CONTROL_MODES: ControlModeInfo[] = [
  {
    mode: 'simple',
    label: 'Simple',
    fire: 'Auto-fire when crosshair on enemy',
    ads: 'Auto-ADS',
    target: 'Casual players',
  },
  {
    mode: 'advanced',
    label: 'Advanced',
    fire: 'Manual fire button',
    ads: 'Manual ADS',
    target: 'Intermediate',
  },
  {
    mode: 'custom',
    label: 'Custom',
    fire: 'Separate hip-fire + ADS-fire',
    ads: 'Hold-to-ADS',
    target: 'Competitive',
  },
];

/**
 * Create a control mode selector UI panel.
 * Reads the current controlMode from settingsStore and allows switching.
 * Emits a settings change event via settingsStore.update() so TouchInputProvider reacts.
 *
 * @returns An object with `element` (the DOM container) and `dispose` (cleanup function).
 */
export function createControlModeSelector(): { element: HTMLDivElement; dispose: () => void } {
  const container = document.createElement('div');
  container.id = 'control-mode-selector';
  Object.assign(container.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.85)',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    fontFamily: 'sans-serif',
    color: '#fff',
    maxWidth: '320px',
  });

  // Title
  const title = document.createElement('div');
  Object.assign(title.style, {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: 'rgba(255,255,255,0.9)',
  });
  title.textContent = 'Control Mode';
  container.appendChild(title);

  const currentMode = settingsStore.get().controlMode;
  const cards: HTMLDivElement[] = [];

  for (const info of CONTROL_MODES) {
    const card = document.createElement('div');
    card.dataset.mode = info.mode;
    const isActive = info.mode === currentMode;

    Object.assign(card.style, {
      padding: '10px 12px',
      borderRadius: '6px',
      border: isActive ? '2px solid rgba(0,180,255,0.8)' : '1px solid rgba(255,255,255,0.2)',
      background: isActive ? 'rgba(0,180,255,0.15)' : 'rgba(255,255,255,0.05)',
      cursor: 'pointer',
      transition: 'border-color 0.2s, background 0.2s',
    });

    // Label row
    const labelRow = document.createElement('div');
    Object.assign(labelRow.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
    });

    const labelEl = document.createElement('span');
    Object.assign(labelEl.style, {
      fontWeight: 'bold',
      fontSize: '13px',
    });
    labelEl.textContent = info.label;

    const targetEl = document.createElement('span');
    Object.assign(targetEl.style, {
      fontSize: '10px',
      color: 'rgba(255,255,255,0.5)',
      textTransform: 'uppercase',
    });
    targetEl.textContent = info.target;

    labelRow.appendChild(labelEl);
    labelRow.appendChild(targetEl);
    card.appendChild(labelRow);

    // Details
    const details = document.createElement('div');
    Object.assign(details.style, {
      fontSize: '11px',
      color: 'rgba(255,255,255,0.6)',
      lineHeight: '1.4',
    });
    details.innerHTML = `<b>Fire:</b> ${info.fire}<br><b>ADS:</b> ${info.ads}`;
    card.appendChild(details);

    // Click handler
    const handleSelect = () => {
      settingsStore.update({ controlMode: info.mode });
      // Update visual state of all cards
      for (const c of cards) {
        const isSel = c.dataset.mode === info.mode;
        c.style.border = isSel ? '2px solid rgba(0,180,255,0.8)' : '1px solid rgba(255,255,255,0.2)';
        c.style.background = isSel ? 'rgba(0,180,255,0.15)' : 'rgba(255,255,255,0.05)';
      }
    };
    card.addEventListener('click', handleSelect);
    card.addEventListener('touchstart', (e) => {
      e.preventDefault();
      handleSelect();
    });

    cards.push(card);
    container.appendChild(card);
  }

  // Listen for external settings changes to keep in sync
  const onSettingsChange = (s: { controlMode: ControlMode }) => {
    for (const c of cards) {
      const isSel = c.dataset.mode === s.controlMode;
      c.style.border = isSel ? '2px solid rgba(0,180,255,0.8)' : '1px solid rgba(255,255,255,0.2)';
      c.style.background = isSel ? 'rgba(0,180,255,0.15)' : 'rgba(255,255,255,0.05)';
    }
  };
  settingsStore.onChange(onSettingsChange as any);

  const dispose = () => {
    settingsStore.offChange(onSettingsChange as any);
    container.remove();
  };

  return { element: container, dispose };
}

/**
 * Get the current control mode from the settings store.
 * Convenience accessor.
 */
export function getControlMode(): ControlMode {
  return settingsStore.get().controlMode;
}

/**
 * Set the control mode and persist to settings.
 * Emits a settings change event.
 */
export function setControlMode(mode: ControlMode): void {
  settingsStore.update({ controlMode: mode });
}
